import React, {useState} from "react";
import {CommandBar} from "@fluentui/react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {v4 as uuidv4} from "uuid";
import {useDispatch} from "react-redux";

import styles from "./ConstructionCommandBar.module.scss";

import IsaacConfirmationModal from "../../../../components/isaacConfirmationModal/IsaacConfirmationModal";
import useConstructionCommandBar from "./useConstructionCommandBar";
import useConstructionNewHook from "../../../../hooks/useConstructionNewHook";
import useCalculationNewHook from "../../../../hooks/useCalculationNewHook";
import {deleteConstructions} from "../../../constructionsNew/constructionsNewSlice";
import {clear} from "../../constructionNewSlice";
import PathConstants from "../../../../routes/pathConstants";
import type ConstructionStatus from "../../../../dto/construction/ConstructionStatus";

interface ConstructionCommandBarProps {
  constructionStatus: ConstructionStatus | undefined;
}

const ConstructionCommandBar: React.FunctionComponent<ConstructionCommandBarProps> = ({constructionStatus}) => {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {t} = useTranslation(["common"]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const {deleteConstructionById} = useConstructionNewHook();
  const {deleteCalculationByConstructionId} = useCalculationNewHook();

  const handleDeleteConfirmationDismiss = (): void => {
    setIsDeleteDialogOpen(prevState => !prevState);
  };

  const handleDeleteConstructions = (): void => {
    handleDeleteConfirmationDismiss();
    if (id) {
      const correlationId: string = uuidv4();

      void Promise.all([
        deleteCalculationByConstructionId(id, correlationId),
        deleteConstructionById(id, correlationId),
      ]);

      dispatch(deleteConstructions([id]));
      dispatch(clear());
      void navigate(PathConstants.Home);
    }
  };

  const {items} = useConstructionCommandBar(constructionStatus, handleDeleteConfirmationDismiss);

  return (
    <div className={styles.commandBarContainer}>
      <CommandBar items={items} />
      <IsaacConfirmationModal
        detailsText={t("deleteConstructionWarning")}
        isOpen={isDeleteDialogOpen}
        onConfirm={handleDeleteConstructions}
        onDismiss={handleDeleteConfirmationDismiss}
      />
    </div>
  );
};

export default ConstructionCommandBar;
